import type { FunctionComponent } from 'react';
import { useLocation } from 'react-router';
import {
  Link,
  newAsyncDataEnvelope,
} from '@dr.pogodin/react-utils';
import { captureException } from '@sentry/react';

import Avatar from 'components/Avatar';

import { logout } from 'services/auth';
import {
  P_AIAPP_EXPERIENCE,
  P_AIAPP_STATS,
  initExperienceData,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import IconPlus from 'assets/images/ai/icon-experience-create.svg';
// import IconHeart from 'assets/images/ai/icon-experience-favorite.svg';
import IconHistory from 'assets/images/ai/icon-experience-previous.svg';
import IconStats from 'assets/images/ai/icon-growth-tracking.svg';
import IconBurger from 'assets/images/ai/icon-hamburger.svg';
import IconLogout from 'assets/images/ai/icon-logout.svg';
import IconProfile from 'assets/images/ai/icon-my-profile.svg';
// import IconBell from 'assets/images/ai/icon-notification.svg';
import IconSidebar from 'assets/images/ai/icon-sidebar.svg';
import Logo from 'assets/images/logo-soul-sanctuary-new-60px.svg';

import './style.scss';

type PropsT = {
  hideMobileToggle?: boolean;
};

const LeftSidebar: FunctionComponent<PropsT> = ({ hideMobileToggle }) => {
  const { pathname } = useLocation();
  const gs = useGlobalStateObject();
  const [user] = useGlobalState('user');
  if (!user) throw Error('Internal error');

  const [isExpanded, setIsExpanded] = useGlobalState('ui.leftSidebarExpanded');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useGlobalState('ui.mobileMenuOpen');

  const userRoles: string[] = [];
  if (user.roles.includes('OWNER')) userRoles.push('Admin');
  if (user.roles.includes('ARTIST')) userRoles.push('Practitioner');
  if (!userRoles.length) userRoles.push('Registered User');

  const handleLogout = async () => {
    try {
      await logout();
      location.href = '/';
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to logout');
    }
  };

  const isDashboardActive = pathname === '/' || pathname === '/check-in';

  const navItems = [
    {
      group: 'Personal',
      items: [
        {
          active: isDashboardActive, icon: <IconProfile />, label: 'Dashboard', to: '/',
        },
        // { icon: <IconBell />, label: 'Notifications', to: '#' },
        {
          active: pathname === P_AIAPP_STATS, icon: <IconStats />, label: 'Growth Tracking', to: P_AIAPP_STATS,
        },
      ],
    },
    {
      group: 'Experiences',
      items: [
        {
          active: pathname === P_AIAPP_EXPERIENCE, icon: <IconPlus />, label: 'Create a new experience', to: P_AIAPP_EXPERIENCE,
        },
        {
          active: pathname === '/past-sessions', icon: <IconHistory />, label: 'My previous experiences', to: '/past-sessions',
        },
        // { icon: <IconHeart />, label: 'Favorite experiences', to: '#' },
      ],
    },
  ];

  return (
    <>
      {hideMobileToggle ? null : (
        <Link
          onClick={() => {
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
          styleName="mobileToggle"
          to="#"
        >
          <IconBurger />
        </Link>
      )}
      <div styleName={`sidebarWrapper ${isExpanded ? 'expanded' : ''} ${isMobileMenuOpen ? 'mobileOpen' : ''}`}>
        <div styleName="sidebarContainer">
          <div styleName={`topSection ${isExpanded ? 'expanded' : ''}`}>
            <div styleName="logoWrapper">
              <Logo styleName="logoSidebar" />
            </div>
            <Link
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              styleName={`toggleButton ${isExpanded ? 'expanded' : ''}`}
              to="#"
            >
              <IconSidebar />
            </Link>
          </div>

          <nav styleName="nav">
            {navItems.map((group) => (
              <div key={group.group} styleName={`navGroup ${isExpanded ? 'expanded' : ''}`}>
                <div styleName="groupLabel">{group.group}</div>
                {group.items.map((item) => (
                  <Link
                    key={item.label}
                    onClick={() => {
                      setIsMobileMenuOpen(false);

                      if (item.to === P_AIAPP_EXPERIENCE) {
                        gs.set(
                          'experience',
                          newAsyncDataEnvelope({
                            checkInId: null,
                            experience: initExperienceData(),
                            sessionKind: null,
                          }, {
                            timestamp: Date.now(),
                          }),
                        );
                      }
                    }}
                    styleName={`navItem ${item.active ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
                    to={item.to}
                  >
                    <span styleName={`icon ${isExpanded ? 'expanded' : ''}`}>{item.icon}</span>
                    <span styleName="label">{item.label}</span>
                  </Link>
                ))}
              </div>
            ))}
          </nav>

          <div styleName={`profileSection ${isExpanded ? 'expanded' : ''}`}>
            <Avatar user={user} />
            <div styleName="nameAndRole">
              <div styleName="name">{user.firstName} {user.lastName}</div>
              <div styleName="role">{userRoles.join(', ')}</div>
            </div>
          </div>

          <div styleName={`bottomSection ${isExpanded ? 'expanded' : ''}`}>
            <Link
              onClick={() => {
                void handleLogout();
              }}
              styleName={`logoutButton ${isExpanded ? 'expanded' : ''}`}
              to="#"
            >
              <span styleName={`icon ${isExpanded ? 'expanded' : ''}`}><IconLogout /></span>
              <span styleName="label">Logout</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeftSidebar;
